import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import * as api from "../api/client";

const ms = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`);
const when = (s) => (s ? new Date(s * 1000).toLocaleString() : "—");
const KIND = { data: "Data", knowledge: "Documents", general: "General" };

export default function AgentsPage() {
  const [roster, setRoster] = useState(null);
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState(null);
  const [filter, setFilter] = useState({ agent: "", status: "" });
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");

  const loadRuns = useCallback(async () => {
    try {
      setRuns((await api.listAgentRuns({ limit: 100, ...filter })).runs);
    } catch (e) {
      setErr(e.message);
    }
  }, [filter]);

  useEffect(() => {
    Promise.all([api.getAgentRoster(), api.getAgentStats(7)])
      .then(([r, s]) => { setRoster(r); setStats(s); })
      .catch((e) => setErr(e.message));
  }, []);
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const perAgent = Object.fromEntries((stats?.perAgent || []).map((p) => [p.agent, p]));
  const errRate = stats && stats.runs ? stats.errors / stats.runs : 0;

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <h1 className="cx-title">Agents</h1>
          <p className="cx-sub">How QLens answers: a supervisor reads every question and routes it to the right specialist agent, which runs governed queries and builds the answer. Every run is traced so you can see who asked what, which agents and tools ran, how long each step took and what failed.</p>
        </div>
      </header>
      {err && <div className="cx-msgbox bad">{err}</div>}

      {stats && (
        <div className="ml-stats">
          <Stat label="Runs · 7 days" value={stats.runs.toLocaleString("en-IN")} sub={`${stats.users} people asked`} primary />
          <Stat label="Error rate" value={`${(errRate * 100).toFixed(1)}%`} sub={`${stats.errors} failed runs`} />
          <Stat label="Average time" value={ms(stats.avgMs)} sub={`95% finish within ${ms(stats.p95Ms)}`} />
          <Stat label="Specialists" value={roster ? roster.workers.length : "—"} sub="agents the supervisor can route to" />
        </div>
      )}

      {roster && (
        <section className="cx-card ag-flow">
          <div className="cx-card-h">Orchestration</div>
          <div className="ag-sup">
            <span className="ag-sup-box"><strong>{roster.supervisor}</strong><small>reads the question, picks the agent, checks the answer</small></span>
          </div>
          <div className="ag-arrow" aria-hidden="true" />
          <div className="ag-workers">
            {roster.workers.map((w) => {
              const p = perAgent[w.name];
              return (
                <button key={w.name} type="button" className={"ag-worker" + (filter.agent === w.name ? " on" : "")}
                        onClick={() => setFilter((f) => ({ ...f, agent: f.agent === w.name ? "" : w.name }))}
                        title={w.description}>
                  <span className={`ag-kind ${w.kind}`}>{KIND[w.kind] || w.kind}</span>
                  <strong>{w.name}</strong>
                  {w.dataset && <code>{w.dataset}</code>}
                  <span className="cx-muted">{p ? `${p.n} runs · avg ${ms(p.avg_ms)}${p.errors ? ` · ${p.errors} errors` : ""}` : "no runs this week"}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="cx-card ag-runs">
        <div className="ag-runs-h">
          <div className="cx-card-h">Run traces</div>
          <div className="ag-filters">
            <select value={filter.agent} onChange={(e) => setFilter((f) => ({ ...f, agent: e.target.value }))}>
              <option value="">All agents</option>
              {(roster?.workers || []).map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
            </select>
            <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
              <option value="">Any status</option><option value="ok">Succeeded</option>
              <option value="error">Failed</option><option value="cancelled">Cancelled</option>
            </select>
            <button type="button" className="cx-btn" onClick={loadRuns}>Refresh</button>
          </div>
        </div>
        {!runs ? <div className="cx-loading"><Spinner animation="border" size="sm" /></div> : runs.length === 0 ? (
          <div className="cx-empty"><strong>No runs yet</strong><span>Ask a question in Chat and its trace appears here.</span></div>
        ) : (
          <div className="cx-scroll">
            <table className="cx-table ag-table">
              <thead><tr><th>When</th><th>User</th><th>Question</th><th>Agents</th><th>Tools</th><th>Time</th><th>Status</th></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="ml-row" onClick={() => setOpen(r.id)}>
                    <td className="cx-muted">{when(r.startedAt)}</td>
                    <td>{r.username}</td>
                    <td className="ag-q">{r.question}</td>
                    <td>{r.agents.map((a) => <span key={a} className="ag-chip">{a}</span>)}</td>
                    <td>{r.toolCalls}</td>
                    <td>{ms(r.durationMs)}</td>
                    <td><span className={`cx-status ${r.status === "ok" ? "ok" : r.status === "error" ? "failed" : ""}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <TraceModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function Stat({ label, value, sub, primary }) {
  return (
    <div className={"cx-card ml-stat" + (primary ? " primary" : "")}>
      <div className="ml-stat-l">{label}</div>
      <div className="ml-stat-v">{value}</div>
      {sub && <div className="cx-muted">{sub}</div>}
    </div>
  );
}

function TraceModal({ id, onClose }) {
  const [run, setRun] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.getAgentRun(id).then(setRun).catch((e) => setErr(e.message));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id, onClose]);
  const total = Math.max(1, run?.durationMs || 1);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="cx-modal ag-trace" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Run trace">
        <div className="cx-modal-h">
          <div>
            <div className="cx-modal-title">Run trace</div>
            <div className="cx-muted">{run ? `${run.username} · ${when(run.startedAt)} · ${ms(run.durationMs)} · model ${run.model}` : ""}</div>
          </div>
          <button type="button" className="cx-x-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cx-modal-body">
          {err && <div className="cx-msgbox bad">{err}</div>}
          {!run ? <div className="cx-loading"><Spinner animation="border" size="sm" /></div> : (
            <>
              <div className="ag-question">{run.question}</div>
              {run.error && <div className="cx-msgbox bad">{run.error}</div>}
              <div className="ag-timeline">
                <div className="ag-step sup">
                  <span className="ag-step-l"><strong>Supervisor</strong> received the question</span>
                  <span className="ag-bar-wrap"><span className="ag-bar sup" style={{ left: 0, width: "100%" }} /></span>
                  <span className="ag-step-t">{ms(run.durationMs)}</span>
                </div>
                {run.steps.map((s, i) => s.kind === "agent" ? (
                  <div key={i} className="ag-step agent">
                    <span className="ag-step-l">{s.state === "delegated" ? "→ routed to" : "✎ answering:"} <strong>{s.agent}</strong></span>
                    <span className="ag-bar-wrap"><span className="ag-tick" style={{ left: `${(s.at / total) * 100}%` }} /></span>
                    <span className="ag-step-t">at {ms(s.at)}</span>
                  </div>
                ) : (
                  <div key={i} className={"ag-step tool " + (s.status || "")}>
                    <span className="ag-step-l">
                      <code>{s.tool}</code>{s.agent && <span className="cx-muted"> · {s.agent}</span>}
                      <details>
                        <summary>details</summary>
                        <div className="ag-io"><b>Input</b><pre>{s.args || "—"}</pre><b>Result</b><pre>{s.result || "—"}</pre></div>
                      </details>
                    </span>
                    <span className="ag-bar-wrap">
                      <span className={"ag-bar " + (s.status === "ok" ? "ok" : "bad")}
                            style={{ left: `${(s.at / total) * 100}%`, width: `${Math.max(1, ((s.ms || 0) / total) * 100)}%` }} />
                    </span>
                    <span className="ag-step-t">{ms(s.ms)}</span>
                  </div>
                ))}
              </div>
              <p className="cx-muted cx-note">Answer: {run.answerChars.toLocaleString()} characters · {run.outputs.charts} charts · {run.outputs.tables} tables · {run.outputs.kpis} KPI sets</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
